// BriefColdStart — sticky-bottom panel on /brief/$id that explains what the
// page lets the user do. W4-C: relocated out of the workspace landing AI
// rail into a per-contract trigger. Shows once per opp per session (keyed
// by opp id in localStorage). No LLM calls (Iron Rule #4).
//
// Layout: sticky-bottom rail, full width, dense terminal styling. A title +
// short copy + 3-4 CTA buttons that scroll/jump to the relevant section of
// the brief detail page (Watchlist add, Bid draft, Case file, Notice URL).
// Closes via the × button at the right edge.

import { markColdStartSeen, shouldShowColdStart } from '@/lib/brief-coldstart';
import { type ReactNode, useEffect, useState } from 'react';

export interface BriefColdStartProps {
  /** Opportunity id — keys the per-session dismissal flag. */
  oppId: string;
  /** Optional CTAs override (for tests / future contract types). */
  actions?: ColdStartAction[];
}

interface ColdStartAction {
  label: string;
  hint?: string;
  onClick: () => void;
}

/** Resolve storage lazily so SSR / non-browser runtimes don't blow up at
 *  module load. Returns null when localStorage isn't reachable. */
function getStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

const DEFAULT_ACTIONS: ColdStartAction[] = [
  {
    label: 'Track this opportunity',
    hint: 'Watchlist surfaces every amendment, Q&A cutoff, award decision.',
    onClick: () => focusByDataAttr('data-action="track"'),
  },
  {
    label: 'Add to pipeline',
    hint: 'Add this opportunity to a pipeline so it shows on your radar.',
    onClick: () => focusByDataAttr('data-action="add-to-pipeline"'),
  },
  {
    label: 'Open the source notice',
    hint: 'Jump straight to the agency-hosted notice PDF / posting.',
    onClick: () => focusFirstNoticeLink(),
  },
  {
    label: 'Browse linked documents',
    hint: 'Scroll to the attached solicitation documents.',
    onClick: () => scrollToText('DOCUMENTS'),
  },
];

function focusByDataAttr(selector: string) {
  if (typeof document === 'undefined') return;
  const el = document.querySelector<HTMLElement>(`[${selector}]`);
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.focus({ preventScroll: true });
  }
}
function focusFirstNoticeLink() {
  if (typeof document === 'undefined') return;
  const el = document.querySelector<HTMLAnchorElement>('a[data-testid="notice-link"]');
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.focus({ preventScroll: true });
  }
}
function scrollToText(label: string) {
  if (typeof document === 'undefined') return;
  // Pane headers (`<span>` inside `.pane-h`) carry section labels.
  const headers = Array.from(document.querySelectorAll<HTMLElement>('.pane-h > span'));
  const match = headers.find((s) => s.textContent?.trim().toUpperCase() === label);
  if (match) {
    match.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

export function BriefColdStart({ oppId, actions }: BriefColdStartProps): ReactNode {
  // Compute initial visibility off storage at mount time. The check is
  // deferred to useEffect so the *first* server-shaped render is consistent
  // (visible=false) and we then flip to `true` once we know the user hasn't
  // dismissed yet — avoids hydration mismatch warnings if anyone wires SSR.
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!oppId) return;
    if (shouldShowColdStart(getStorage(), oppId)) setVisible(true);
  }, [oppId]);

  if (!visible) return null;

  const dismiss = () => {
    markColdStartSeen(getStorage(), oppId);
    setVisible(false);
  };

  const items = actions ?? DEFAULT_ACTIONS;

  return (
    <section
      data-component="BriefColdStart"
      aria-label="getting started with this contract"
      style={{
        position: 'sticky',
        bottom: 0,
        zIndex: 5,
        background: 'var(--bg-pane-2, #141414)',
        borderTop: '1px solid var(--bd-strong)',
        padding: '10px 16px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 16,
        fontFamily: 'var(--mono)',
      }}
    >
      <div style={{ flexShrink: 0, paddingTop: 2 }}>
        <span
          style={{
            color: 'var(--amber, #ffb000)',
            fontSize: 10,
            letterSpacing: 0.4,
          }}
        >
          GETTING STARTED
        </span>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p
          style={{
            margin: 0,
            color: 'var(--fg-2)',
            fontSize: 11,
            lineHeight: 1.5,
          }}
        >
          This is the contract brief — every public field, attached document, linked award, and
          similar opportunity in one place. Pick a starting point.
        </p>
        <div
          style={{
            marginTop: 8,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8,
          }}
        >
          {items.map((a) => (
            <button
              key={a.label}
              type="button"
              onClick={a.onClick}
              title={a.hint ?? a.label}
              data-testid="brief-coldstart-action"
              style={{
                background: 'transparent',
                border: '1px solid var(--bd-strong)',
                color: 'var(--fg-1)',
                cursor: 'pointer',
                fontFamily: 'var(--mono)',
                fontSize: 11,
                padding: '4px 10px',
                letterSpacing: 0.3,
              }}
            >
              {a.label}
            </button>
          ))}
        </div>
      </div>
      <button
        type="button"
        onClick={dismiss}
        aria-label="dismiss getting-started panel"
        title="dismiss"
        data-testid="brief-coldstart-dismiss"
        style={{
          background: 'transparent',
          border: 'none',
          color: 'var(--fg-3)',
          cursor: 'pointer',
          fontSize: 16,
          padding: '0 4px',
          alignSelf: 'flex-start',
        }}
      >
        ×
      </button>
    </section>
  );
}
